import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import apiRequest from '../utils/api';

interface SupportRequest {
  id: number;
  taskId: string | null;
  username: string;
  idSmm: string;
  startCount: string | null;
  serviceId: string | null;
  actions: string;
  status: string;
  note: string | null;
  noteExtra: string | null;
  errorMessage: string | null;
  idApi: string | null;
  provider: string | null;
  timeCreated: string;
  timeUpdated: string | null;
}

const STATUS_OPTIONS = ['pending', 'processing', 'completed', 'manual_review', 'fail', 'wait_zalo', 'zalo_sent'];
const ACTION_OPTIONS = ['cancel', 'refill', 'speed_up', 'fake_complete', 'check_status', 'update', 'other'];

const SupportRequests: React.FC = () => {
  const { hasPermission } = useAuth();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [actionsFilter, setActionsFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('processing');

  // Status counts
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  // Detail modal
  const [detailRequest, setDetailRequest] = useState<SupportRequest | null>(null);

  // Loss cancel modal
  const [lossCancelModal, setLossCancelModal] = useState<{
    open: boolean;
    items: Array<{ id: number; idSmm: string }>;
    phase: 'checking' | 'ready' | 'processing' | 'done';
    eligible: Array<{ id: number; idSmm: string; orderStatus: string }>;
    skipped: Array<{ id: number; idSmm: string; reason: string }>;
    results: Array<{ idSmm: string; success: boolean; message: string }>;
    currentIndex: number;
  }>({
    open: false, items: [], phase: 'checking', eligible: [], skipped: [], results: [], currentIndex: 0
  });

  // Tab: 'requests' | 'loss-stats'
  const [activeTab, setActiveTab] = useState<'requests' | 'loss-stats'>('requests');
  const [lossStats, setLossStats] = useState<{ rows: any[]; totals: any } | null>(null);
  const [lossLoading, setLossLoading] = useState(false);

  // Notification
  const [notification, setNotification] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({ show: false, type: 'success', message: '' });

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: 'success', message: '' }), 3000);
  };

  const fetchRequests = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true); else setPageLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });
      if (statusFilter) params.append('status', statusFilter);
      if (actionsFilter) params.append('actions', actionsFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await apiRequest(`/api/support-requests?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setRequests(data.data || []);
        setTotalItems(data.pagination?.total || 0);
        setStatusCounts(data.statusCounts || {});
        setSelectedIds(new Set());
      } else {
        setError('Không thể tải danh sách');
      }
    } catch {
      setError('Lỗi kết nối server');
    } finally {
      if (isInitial) setLoading(false); else setPageLoading(false);
    }
  }, [currentPage, itemsPerPage, statusFilter, actionsFilter, searchTerm]);

  useEffect(() => { fetchRequests(true); }, []);
  useEffect(() => {
    if (!loading) {
      // Fetch with the new search on page 1, not with the previous page number.
      if (currentPage !== 1) setCurrentPage(1);
      else fetchRequests(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, actionsFilter, searchTerm]);
  useEffect(() => {
    if (!loading) fetchRequests(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage]);

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; color: string }> = {
      pending: { bg: '#fef3c7', color: '#92400e' },
      processing: { bg: '#dbeafe', color: '#1e40af' },
      completed: { bg: '#d1fae5', color: '#065f46' },
      manual_review: { bg: '#f3e8ff', color: '#7c3aed' },
      fail: { bg: '#fee2e2', color: '#991b1b' },
      wait_zalo: { bg: '#fff7ed', color: '#c2410c' },
      zalo_sent: { bg: '#e0f2fe', color: '#0369a1' },
    };
    const s = styles[status] || { bg: '#f3f4f6', color: '#6b7280' };
    return (
      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: s.bg, color: s.color }}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    const dt = new Date(dateString);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(dt.getUTCDate())}/${pad(dt.getUTCMonth() + 1)}/${dt.getUTCFullYear()} ${pad(dt.getUTCHours())}:${pad(dt.getUTCMinutes())}`;
  };

  const handleSelectAll = () => {
    if (selectedIds.size === requests.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(requests.map(r => r.id)));
    }
  };

  const toggleSelect = (id: number) => {
    const n = new Set(selectedIds);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelectedIds(n);
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;
    try {
      setPageLoading(true);
      const response = await apiRequest('/api/support-requests/bulk/status', {
        method: 'PUT',
        body: JSON.stringify({ ids: Array.from(selectedIds), status: bulkStatus }),
      });
      if (response.ok) {
        const data = await response.json();
        showNotification('success', data.message || 'Cập nhật thành công');
        await fetchRequests(false);
      } else {
        showNotification('error', 'Lỗi cập nhật');
      }
    } catch {
      showNotification('error', 'Lỗi kết nối');
    } finally {
      setPageLoading(false);
    }
  };

  const handleBulkUpdateWithStatus = async (status: string) => {
    if (selectedIds.size === 0) return;
    try {
      setPageLoading(true);
      const response = await apiRequest('/api/support-requests/bulk/status', {
        method: 'PUT',
        body: JSON.stringify({ ids: Array.from(selectedIds), status }),
      });
      if (response.ok) {
        const data = await response.json();
        showNotification('success', data.message || 'Cập nhật thành công');
        await fetchRequests(false);
      } else {
        showNotification('error', 'Lỗi cập nhật');
      }
    } catch {
      showNotification('error', 'Lỗi kết nối');
    } finally {
      setPageLoading(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const response = await apiRequest(`/api/support-requests/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
        showNotification('success', `Đã cập nhật #${id}`);
      } else {
        showNotification('error', 'Lỗi cập nhật');
      }
    } catch {
      showNotification('error', 'Lỗi kết nối');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(`Xóa yêu cầu #${id}?`)) return;
    try {
      const response = await apiRequest(`/api/support-requests/${id}`, { method: 'DELETE' });
      if (response.ok) {
        showNotification('success', 'Đã xóa');
        await fetchRequests(false);
      } else {
        showNotification('error', 'Lỗi xóa');
      }
    } catch {
      showNotification('error', 'Lỗi kết nối');
    }
  };

  // Open loss cancel modal (single or bulk)
  const openLossCancelModal = async (items: Array<{ id: number; idSmm: string }>) => {
    setLossCancelModal({ open: true, items, phase: 'checking', eligible: [], skipped: [], results: [], currentIndex: 0 });

    // Pre-check: fetch order statuses
    try {
      const idSmms = items.map(i => i.idSmm);
      const response = await apiRequest('/api/support-requests/pre-check-cancel', {
        method: 'POST',
        body: JSON.stringify({ idSmms }),
      });
      const data = await response.json();
      if (response.ok) {
        const eligible: Array<{ id: number; idSmm: string; orderStatus: string }> = [];
        const skipped: Array<{ id: number; idSmm: string; reason: string }> = [];
        for (const item of items) {
          const check = data.orders?.find((o: any) => String(o.idSmm) === String(item.idSmm));
          if (!check) {
            skipped.push({ ...item, reason: 'Không tìm thấy đơn hàng' });
          } else if (['refund', 'completed', 'canceled', 'partial'].includes(check.status)) {
            skipped.push({ ...item, reason: `Đã ở trạng thái: ${check.status}` });
          } else {
            eligible.push({ ...item, orderStatus: check.status });
          }
        }
        setLossCancelModal(prev => ({ ...prev, phase: 'ready', eligible, skipped }));
      } else {
        setLossCancelModal(prev => ({
          ...prev, phase: 'ready', eligible: [],
          skipped: items.map(i => ({ ...i, reason: data.error || 'Lỗi kiểm tra' }))
        }));
      }
    } catch {
      setLossCancelModal(prev => ({
        ...prev, phase: 'ready', eligible: [],
        skipped: items.map(i => ({ ...i, reason: 'Lỗi kết nối' }))
      }));
    }
  };

  // Process loss cancel one by one
  const processLossCancel = async () => {
    const { eligible } = lossCancelModal;
    if (eligible.length === 0) return;

    setLossCancelModal(prev => ({ ...prev, phase: 'processing', currentIndex: 0, results: [] }));

    const results: Array<{ idSmm: string; success: boolean; message: string }> = [];
    for (let i = 0; i < eligible.length; i++) {
      const item = eligible[i];
      setLossCancelModal(prev => ({ ...prev, currentIndex: i }));
      try {
        const response = await apiRequest(`/api/support-requests/${item.id}/cancel-order`, { method: 'POST' });
        const data = await response.json();
        results.push({
          idSmm: item.idSmm,
          success: response.ok,
          message: response.ok ? (data.message || 'OK') : (data.error || 'Lỗi'),
        });
      } catch {
        results.push({ idSmm: item.idSmm, success: false, message: 'Lỗi kết nối' });
      }
      setLossCancelModal(prev => ({ ...prev, results: [...results] }));
    }

    setLossCancelModal(prev => ({ ...prev, phase: 'done', currentIndex: eligible.length }));
    await fetchRequests(false);
  };

  const closeLossCancelModal = () => {
    setLossCancelModal({ open: false, items: [], phase: 'checking', eligible: [], skipped: [], results: [], currentIndex: 0 });
  };

  const fetchLossStats = async () => {
    try {
      setLossLoading(true);
      const response = await apiRequest('/api/support-requests/loss-stats');
      if (response.ok) {
        const data = await response.json();
        setLossStats(data.data);
      }
    } catch {
      showNotification('error', 'Lỗi tải thống kê');
    } finally {
      setLossLoading(false);
    }
  };

  const copyText = (text: string) => {
    navigator.clipboard.writeText(text).then(() => showNotification('success', `Đã copy: ${text}`));
  };

  const thStyle: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', whiteSpace: 'nowrap' };
  const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: '13px', color: '#374151' };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        {/* Notification */}
        {notification.show && (
          <div style={{
            position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
            padding: '12px 20px', borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: notification.type === 'success' ? '#065f46' : '#991b1b',
            fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            {notification.message}
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
            📋 Support Requests
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
            Quản lý yêu cầu hỗ trợ khách hàng
          </p>
        </div>

        {/* Status Count Cards */}
        <div style={{
          display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap'
        }}>
          {[
            { key: 'pending', label: 'Pending', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
            { key: 'processing', label: 'Processing', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
            { key: 'completed', label: 'Completed', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' },
            { key: 'manual_review', label: 'Manual Review', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
            { key: 'fail', label: 'Fail', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
            { key: 'wait_zalo', label: 'Wait Zalo', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa' },
            { key: 'zalo_sent', label: 'Zalo Sent', color: '#0284c7', bg: '#e0f2fe', border: '#bae6fd' },
          ].map(item => (
            <div key={item.key}
              onClick={() => setStatusFilter(statusFilter === item.key ? '' : item.key)}
              style={{
                padding: '12px 20px', borderRadius: '10px', border: `2px solid ${statusFilter === item.key ? item.color : item.border}`,
                backgroundColor: item.bg, cursor: 'pointer', textAlign: 'center', minWidth: '110px',
                transition: 'all 0.2s', boxShadow: statusFilter === item.key ? `0 2px 8px ${item.color}30` : 'none'
              }}
            >
              <div style={{ fontSize: '24px', fontWeight: '800', color: item.color, lineHeight: '1.2' }}>
                {(statusCounts[item.key] || 0).toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', fontWeight: '600', color: item.color, marginTop: '4px', opacity: 0.85 }}>
                {item.label}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px' }}>
          <button onClick={() => setActiveTab('requests')}
            style={{
              padding: '10px 20px', borderRadius: '8px 8px 0 0', border: '1px solid #e2e8f0',
              borderBottom: activeTab === 'requests' ? '2px solid #2563eb' : '1px solid #e2e8f0',
              backgroundColor: activeTab === 'requests' ? 'white' : '#f8fafc',
              color: activeTab === 'requests' ? '#2563eb' : '#6b7280',
              fontWeight: activeTab === 'requests' ? '600' : '400',
              cursor: 'pointer', fontSize: '14px'
            }}>
            📋 Yêu cầu hỗ trợ
          </button>
          <button onClick={() => { setActiveTab('loss-stats'); fetchLossStats(); }}
            style={{
              padding: '10px 20px', borderRadius: '8px 8px 0 0', border: '1px solid #e2e8f0',
              borderBottom: activeTab === 'loss-stats' ? '2px solid #dc2626' : '1px solid #e2e8f0',
              backgroundColor: activeTab === 'loss-stats' ? 'white' : '#f8fafc',
              color: activeTab === 'loss-stats' ? '#dc2626' : '#6b7280',
              fontWeight: activeTab === 'loss-stats' ? '600' : '400',
              cursor: 'pointer', fontSize: '14px'
            }}>
            📉 Thống kê hủy lỗ
          </button>
        </div>

        {activeTab === 'requests' && (<>
        {/* Filters */}
        <div style={{
          display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center',
          padding: '12px 16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0'
        }}>
          <input
            type="text"
            aria-label="Tìm kiếm yêu cầu hỗ trợ"
            placeholder="Tìm kiếm... Nhiều ID SMM: 123,456,789"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', minWidth: '200px', flex: '1 1 300px' }}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}>
            <option value="">Tất cả trạng thái</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={actionsFilter} onChange={(e) => setActionsFilter(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}>
            <option value="">Tất cả actions</option>
            {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>
            Tổng: <strong>{totalItems.toLocaleString()}</strong>
          </span>
        </div>

        {/* Bulk actions */}
        {selectedIds.size > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
            padding: '10px 16px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600' }}>
              Đã chọn {selectedIds.size} yêu cầu
            </span>
            <button onClick={() => { setBulkStatus('completed'); handleBulkUpdateWithStatus('completed'); }} disabled={pageLoading}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: 'none',
                backgroundColor: '#059669', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
              }}>
              ✅ Hoàn tất
            </button>
            <button onClick={() => { setBulkStatus('failed'); handleBulkUpdateWithStatus('failed'); }} disabled={pageLoading}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: 'none',
                backgroundColor: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
              }}>
              ❌ Reject
            </button>
            <button onClick={() => {
              const items = requests.filter(r => selectedIds.has(r.id)).map(r => ({ id: r.id, idSmm: r.idSmm }));
              openLossCancelModal(items);
            }} disabled={pageLoading}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: 'none',
                backgroundColor: '#f97316', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
              }}>
              ⚠️ Hủy hoàn (lỗ)
            </button>
            <div style={{ width: '1px', height: '20px', backgroundColor: '#bfdbfe' }}></div>
            <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #93c5fd', fontSize: '13px' }}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={handleBulkUpdate} disabled={pageLoading}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: 'none',
                backgroundColor: '#2563eb', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
              }}>
              Cập nhật
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: '1px solid #d1d5db',
                backgroundColor: 'white', color: '#6b7280', cursor: 'pointer', fontSize: '13px'
              }}>
              Bỏ chọn
            </button>
            <div style={{ width: '1px', height: '20px', backgroundColor: '#bfdbfe' }}></div>
            <button onClick={() => {
              const selectedReqs = requests.filter(r => selectedIds.has(r.id));
              const idSmms = selectedReqs.map(r => r.idSmm).join(',');
              navigator.clipboard.writeText(idSmms).then(() => showNotification('success', `Đã copy ${selectedReqs.length} ID SMM`));
            }}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: '1px solid #93c5fd',
                backgroundColor: 'white', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
              }}>
              📋 Copy ID SMM
            </button>
            <button onClick={() => {
              const selectedReqs = requests.filter(r => selectedIds.has(r.id));
              const ids = selectedReqs.map(r => r.id).join(',');
              navigator.clipboard.writeText(ids).then(() => showNotification('success', `Đã copy ${selectedReqs.length} ID`));
            }}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: '1px solid #93c5fd',
                backgroundColor: 'white', color: '#2563eb', cursor: 'pointer', fontSize: '13px', fontWeight: '500'
              }}>
              📋 Copy ID
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: '12px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ marginTop: '10px', color: '#6b7280' }}>Đang tải...</p>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0',
            overflowX: 'auto', position: 'relative',
            opacity: pageLoading ? 0.6 : 1, transition: 'opacity 0.2s'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1400px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>
                    <input type="checkbox" checked={selectedIds.size === requests.length && requests.length > 0} onChange={handleSelectAll} />
                  </th>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Task ID</th>
                  <th style={thStyle}>Username</th>
                  <th style={thStyle}>ID SMM</th>
                  <th style={thStyle}>Start Count</th>
                  <th style={thStyle}>Service ID</th>
                  <th style={thStyle}>Actions</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Note</th>
                  <th style={thStyle}>Error</th>
                  <th style={thStyle}>ID API</th>
                  <th style={thStyle}>Provider</th>
                  <th style={thStyle}>Ngày tạo</th>
                  <th style={thStyle}>Cập nhật</th>
                  <th style={thStyle}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={16} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                      Không có yêu cầu hỗ trợ nào
                    </td>
                  </tr>
                ) : (
                  requests.map(req => (
                    <tr key={req.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: selectedIds.has(req.id) ? '#eff6ff' : 'white',
                      cursor: 'pointer'
                    }}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, select, input')) return;
                        toggleSelect(req.id);
                      }}
                    >
                      <td style={tdStyle}>
                        <input type="checkbox" checked={selectedIds.has(req.id)} readOnly style={{ pointerEvents: 'none' }} />
                      </td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>#{req.id}</td>
                      <td style={{ ...tdStyle, fontSize: '12px', color: '#6366f1', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); if (req.taskId) copyText(req.taskId); }}>
                        {req.taskId || '—'}
                      </td>
                      <td style={tdStyle}>{req.username}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', cursor: 'pointer', color: '#2563eb' }}
                        onClick={(e) => { e.stopPropagation(); copyText(req.idSmm); }}>
                        {req.idSmm}
                      </td>
                      <td style={tdStyle}>{req.startCount || '—'}</td>
                      <td style={tdStyle}>{req.serviceId || '—'}</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '500',
                          backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0'
                        }}>
                          {req.actions}
                        </span>
                      </td>
                      <td style={tdStyle}>{getStatusBadge(req.status)}</td>
                      <td style={{ ...tdStyle, maxWidth: '250px', minWidth: '150px' }}>
                        {req.note ? (
                          <div style={{
                            padding: '6px 8px', backgroundColor: '#f8fafc', borderRadius: '4px', border: '1px solid #e2e8f0',
                            fontSize: '12px', lineHeight: '1.4', maxHeight: '80px', overflowY: 'auto',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                          }}>
                            {req.note}
                          </div>
                        ) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '250px', minWidth: '150px' }}>
                        {req.errorMessage ? (
                          <div style={{
                            padding: '6px 8px', backgroundColor: '#fff1f2', borderRadius: '4px', border: '1px solid #fecaca',
                            fontSize: '12px', lineHeight: '1.4', maxHeight: '80px', overflowY: 'auto',
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#991b1b'
                          }}>
                            {req.errorMessage}
                          </div>
                        ) : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '12px', fontFamily: 'monospace', maxWidth: '80px' }}>
                        {req.idApi ? (
                          <span
                            title={req.idApi}
                            onClick={(e) => { e.stopPropagation(); copyText(req.idApi!); }}
                            style={{ cursor: 'pointer', color: '#6366f1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '80px' }}
                          >
                            {req.idApi}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={tdStyle}>{req.provider || '—'}</td>
                      <td style={{ ...tdStyle, fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(req.timeCreated)}</td>
                      <td style={{ ...tdStyle, fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(req.timeUpdated)}</td>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                          {req.status !== 'completed' && (
                            <button onClick={() => handleStatusChange(req.id, 'completed')}
                              title="Hoàn tất"
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #a7f3d0', backgroundColor: '#d1fae5', color: '#065f46', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                              ✅
                            </button>
                          )}
                          {req.status !== 'failed' && (
                            <button onClick={() => handleStatusChange(req.id, 'failed')}
                              title="Reject"
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #fecaca', backgroundColor: '#fee2e2', color: '#991b1b', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                              ❌
                            </button>
                          )}
                          <button onClick={() => openLossCancelModal([{ id: req.id, idSmm: req.idSmm }])}
                            title="Hủy hoàn (lỗ)"
                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #fed7aa', backgroundColor: '#fff7ed', color: '#c2410c', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                            ⚠️
                          </button>
                          <button onClick={() => setDetailRequest(req)}
                            style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#3b82f6', cursor: 'pointer', fontSize: '12px' }}>
                            📋
                          </button>
                          <select value={req.status} onChange={(e) => handleStatusChange(req.id, e.target.value)}
                            style={{ padding: '4px 6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', maxWidth: '90px' }}>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          {hasPermission('settings', 'delete') && (
                            <button onClick={() => handleDelete(req.id)}
                              title="Xóa"
                              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}>
                              🗑
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalItems > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: '16px', padding: '12px 16px', backgroundColor: 'white',
            borderRadius: '8px', border: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Hiển thị:</span>
              <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}>
                {[20, 50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} / {totalItems}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', backgroundColor: currentPage === 1 ? '#f9fafb' : 'white', fontSize: '13px' }}>
                ⟪
              </button>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', backgroundColor: currentPage === 1 ? '#f9fafb' : 'white', fontSize: '13px' }}>
                ‹
              </button>
              <span style={{ padding: '4px 12px', fontSize: '13px', color: '#374151' }}>{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', backgroundColor: currentPage >= totalPages ? '#f9fafb' : 'white', fontSize: '13px' }}>
                ›
              </button>
              <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}
                style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', backgroundColor: currentPage >= totalPages ? '#f9fafb' : 'white', fontSize: '13px' }}>
                ⟫
              </button>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {detailRequest && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => setDetailRequest(null)}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>
                  Chi tiết yêu cầu #{detailRequest.id}
                </h2>
                <button onClick={() => setDetailRequest(null)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', backgroundColor: '#f3f4f6', cursor: 'pointer', fontSize: '16px' }}>
                  ✕
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {[
                  { label: 'Task ID', value: detailRequest.taskId },
                  { label: 'Username', value: detailRequest.username },
                  { label: 'ID SMM', value: detailRequest.idSmm },
                  { label: 'Start Count', value: detailRequest.startCount },
                  { label: 'Service ID', value: detailRequest.serviceId },
                  { label: 'Actions', value: detailRequest.actions },
                  { label: 'Status', value: detailRequest.status },
                  { label: 'ID API', value: detailRequest.idApi },
                  { label: 'Provider', value: detailRequest.provider },
                  { label: 'Ngày tạo', value: formatDate(detailRequest.timeCreated) },
                  { label: 'Cập nhật', value: formatDate(detailRequest.timeUpdated) },
                ].map((item, i) => (
                  <div key={i}>
                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px' }}>{item.label}</label>
                    <div style={{ fontSize: '14px', color: '#1e293b' }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>

              {detailRequest.note && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Note</label>
                  <div style={{ fontSize: '13px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailRequest.note}
                  </div>
                </div>
              )}

              {detailRequest.noteExtra && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Note Extra</label>
                  <div style={{ fontSize: '13px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailRequest.noteExtra}
                  </div>
                </div>
              )}

              {detailRequest.errorMessage && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#dc2626', display: 'block', marginBottom: '4px' }}>Error Message</label>
                  <div style={{ fontSize: '13px', padding: '10px', backgroundColor: '#fff1f2', borderRadius: '6px', color: '#991b1b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {detailRequest.errorMessage}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Loss Cancel Progress Modal */}
        {lossCancelModal.open && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
            onClick={() => lossCancelModal.phase !== 'processing' && closeLossCancelModal()}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '580px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }}
              onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div style={{ fontSize: '36px', marginBottom: '4px' }}>⚠️</div>
                <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>
                  Hủy hoàn (LỖ) — {lossCancelModal.items.length} yêu cầu
                </h3>
              </div>

              {/* Phase: Checking */}
              {lossCancelModal.phase === 'checking' && (
                <div style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
                  <div className="loading-spinner" style={{ width: '32px', height: '32px', margin: '0 auto 12px' }}></div>
                  Đang kiểm tra trạng thái đơn hàng...
                </div>
              )}

              {/* Phase: Ready */}
              {lossCancelModal.phase === 'ready' && (
                <>
                  {/* Skipped orders */}
                  {lossCancelModal.skipped.length > 0 && (
                    <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '6px' }}>
                        ⏭️ Bỏ qua ({lossCancelModal.skipped.length}):
                      </div>
                      {lossCancelModal.skipped.map((s, i) => (
                        <div key={i} style={{ fontSize: '12px', color: '#94a3b8', padding: '2px 0' }}>
                          #{s.idSmm} — <span style={{ color: '#ef4444' }}>{s.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Eligible orders */}
                  {lossCancelModal.eligible.length > 0 ? (
                    <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#fff7ed', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#c2410c', marginBottom: '6px' }}>
                        🔥 Sẽ hủy lỗ ({lossCancelModal.eligible.length}):
                      </div>
                      {lossCancelModal.eligible.map((e, i) => (
                        <div key={i} style={{ fontSize: '12px', color: '#9a3412', padding: '2px 0' }}>
                          #{e.idSmm} — hiện tại: <strong>{e.orderStatus}</strong>
                        </div>
                      ))}
                      <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#dc2626', fontWeight: '600' }}>
                        ⚠️ Không hoàn tiền từ provider. Tiền sẽ BỊ LỖ!
                      </p>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '14px' }}>
                      Không có đơn hàng nào đủ điều kiện hủy lỗ
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={closeLossCancelModal}
                      style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' }}>
                      Đóng
                    </button>
                    {lossCancelModal.eligible.length > 0 && (
                      <button onClick={processLossCancel}
                        style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        🔥 Xác nhận hủy lỗ {lossCancelModal.eligible.length} đơn
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* Phase: Processing */}
              {lossCancelModal.phase === 'processing' && (
                <div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', marginBottom: '6px' }}>
                      <span>Đang xử lý...</span>
                      <span>{lossCancelModal.results.length}/{lossCancelModal.eligible.length}</span>
                    </div>
                    <div style={{ height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: '4px', transition: 'width 0.3s',
                        backgroundColor: '#f97316',
                        width: `${(lossCancelModal.results.length / lossCancelModal.eligible.length) * 100}%`
                      }}></div>
                    </div>
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {lossCancelModal.results.map((r, i) => (
                      <div key={i} style={{ fontSize: '12px', padding: '3px 0', color: r.success ? '#16a34a' : '#dc2626' }}>
                        {r.success ? '✅' : '❌'} #{r.idSmm} — {r.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Phase: Done */}
              {lossCancelModal.phase === 'done' && (
                <div>
                  <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '4px' }}>✅</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                      Hoàn tất: {lossCancelModal.results.filter(r => r.success).length} thành công,{' '}
                      {lossCancelModal.results.filter(r => !r.success).length} thất bại
                    </div>
                  </div>
                  <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: '16px' }}>
                    {lossCancelModal.results.map((r, i) => (
                      <div key={i} style={{ fontSize: '12px', padding: '3px 0', color: r.success ? '#16a34a' : '#dc2626' }}>
                        {r.success ? '✅' : '❌'} #{r.idSmm} — {r.message}
                      </div>
                    ))}
                    {lossCancelModal.skipped.length > 0 && (
                      <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px' }}>
                        {lossCancelModal.skipped.map((s, i) => (
                          <div key={i} style={{ fontSize: '12px', padding: '2px 0', color: '#94a3b8' }}>
                            ⏭️ #{s.idSmm} — {s.reason}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button onClick={closeLossCancelModal}
                      style={{ padding: '8px 24px', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                      Đóng
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        </>)}

        {/* Loss Stats Tab */}
        {activeTab === 'loss-stats' && (
          <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>
              📉 Thống kê hủy lỗ
            </h2>

            {lossLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Đang tải...</div>
            ) : lossStats ? (
              <>
                {/* Totals */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                  <div style={{ padding: '16px 24px', backgroundColor: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca', minWidth: '140px' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>{lossStats.totals.totalOrders.toLocaleString()}</div>
                    <div style={{ fontSize: '12px', color: '#991b1b', fontWeight: '600' }}>Tổng đơn hủy lỗ</div>
                  </div>
                  <div style={{ padding: '16px 24px', backgroundColor: '#fff7ed', borderRadius: '10px', border: '1px solid #fed7aa', minWidth: '140px' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#ea580c' }}>{lossStats.totals.totalRemains?.toLocaleString() || 0}</div>
                    <div style={{ fontSize: '12px', color: '#c2410c', fontWeight: '600' }}>Tổng SL còn lại (lỗ)</div>
                  </div>
                  <div style={{ padding: '16px 24px', backgroundColor: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca', minWidth: '180px' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#dc2626' }}>{lossStats.totals.totalLossAmountApi?.toLocaleString() || 0} đ</div>
                    <div style={{ fontSize: '12px', color: '#991b1b', fontWeight: '600' }}>Lỗ tiền API (giá gốc)</div>
                  </div>
                  <div style={{ padding: '16px 24px', backgroundColor: '#f5f3ff', borderRadius: '10px', border: '1px solid #ddd6fe', minWidth: '180px' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#7c3aed' }}>{lossStats.totals.totalLossAmountSystem?.toLocaleString() || 0} đ</div>
                    <div style={{ fontSize: '12px', color: '#6d28d9', fontWeight: '600' }}>Lỗ tiền hệ thống (giá bán)</div>
                  </div>
                </div>

                {/* Single table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#fef2f2', borderBottom: '2px solid #fecaca' }}>
                        <th style={{ ...thStyle, color: '#991b1b' }}>Provider</th>
                        <th style={{ ...thStyle, color: '#991b1b' }}>Service ID</th>
                        <th style={{ ...thStyle, color: '#991b1b', textAlign: 'right' }}>Số đơn</th>
                        <th style={{ ...thStyle, color: '#991b1b', textAlign: 'right' }}>SL còn lại (lỗ)</th>
                        <th style={{ ...thStyle, color: '#991b1b', textAlign: 'right' }}>Lỗ API (VND)</th>
                        <th style={{ ...thStyle, color: '#991b1b', textAlign: 'right' }}>Lỗ hệ thống (VND)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lossStats.rows?.length === 0 ? (
                        <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>Chưa có dữ liệu hủy lỗ</td></tr>
                      ) : (
                        lossStats.rows?.map((r: any, i: number) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ ...tdStyle, fontWeight: '600' }}>{r.provider}</td>
                            <td style={{ ...tdStyle, fontFamily: 'monospace', fontWeight: '600', color: '#2563eb' }}>{r.serviceId}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{r.count.toLocaleString()}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#ea580c', fontWeight: '600' }}>{r.totalRemains.toLocaleString()}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#dc2626', fontWeight: '700' }}>{r.lossAmountApi.toLocaleString()} đ</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#7c3aed', fontWeight: '700' }}>{r.lossAmountSystem.toLocaleString()} đ</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Nhấn tab để tải dữ liệu</div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SupportRequests;
